import ForumLogoText from '@public/images/ForumLogoText.svg'
import Logo from '@public/images/ForummLogo.svg'
import { useMutation } from '@apollo/client'
import { Button } from '@components/inputs/Button'
import { Form } from '@components/inputs/Form'
import { TextInput } from '@components/inputs/TextInput'
import React, { useEffect, useState, useRef } from 'react'
import { useAuth } from '@libs/Auth/useAuth'
import Text from '@components/base/Text'
import Link from 'next/link'
import Box from '@components/base/Box'
import { useRouter } from 'next/router'
import { useTheme } from '@libs/useTheme'
import { GoogleAds } from '@libs/GoogleAdsManager'
import ReCAPTCHA from 'react-google-recaptcha'
import useLocalStorage from '@libs/useLocalStorage'
import { FaLinkedinIn } from 'react-icons/fa'
import { FcGoogle } from 'react-icons/fc'
import { REQUEST_ACCOUNT_CREATION } from '@graphql/users/requestAccountCreation'
import {
  FunnelIntent,
  parseFunnelIntent,
} from '@components/base/GoogleTags'

const genericError =
  "Sorry, we couldn't create an account with that email. Please try again."

const CAPTCHA_SITE_KEY = process.env.NEXT_PUBLIC_CAPTCHA_SITE_KEY

const SignUpForm = () => {
  const Captcha = ReCAPTCHA as any
  const { theme } = useTheme()
  const {
    signInWithGoogle,
    signInWithLinkedIn,
    getHostedAuthenticatedUser,
    currentUser,
  } = useAuth()
  const router = useRouter()
  const [loading, setLoading] = useState<boolean>(false)
  const [completed, setCompleted] = useState<boolean>(false)
  const [completedCaptcha, setCaptchaComplete] =
    useState<boolean>(!CAPTCHA_SITE_KEY)
  const [errors, setErrors] = useState<
    { step: string; message: string; eleId?: string; alert?: boolean }[]
  >([])

  const [isFirstLogin, setFirstLoginFlag] = useLocalStorage(
    'Forumm_FirstLogin',
    false
  )

  const [, setRedirectTarget] = useLocalStorage<
    | {
        path?: string
        analyticsIntent?: FunnelIntent
      }
    | undefined
  >('Forumm_redirect_target', undefined)

  const [requestAccountCreation] = useMutation(REQUEST_ACCOUNT_CREATION)

  const funnelIntent =
    parseFunnelIntent(router.query?.intent) ?? 'create_event'
  const requestedRedirect =
    typeof router.query?.redirect === 'string' ? router.query.redirect : ''
  const safeRedirect =
    requestedRedirect.startsWith('/') && !requestedRedirect.startsWith('//')
      ? requestedRedirect
      : ''
  const redirectPath =
    safeRedirect || (funnelIntent === 'create_event' ? '/create-event' : '')
  const loginHref = {
    pathname: '/login',
    query: {
      intent: funnelIntent,
      ...(redirectPath ? { redirect: redirectPath } : {}),
    },
  }

  const redirectAfterLogin = () => {
    router.replace(
      isFirstLogin || funnelIntent === 'create_event'
        ? '/create-event'
        : safeRedirect || '/dashboard'
    )
  }

  const beginHostedSignIn = async (signIn: () => Promise<unknown>) => {
    setRedirectTarget({
      ...(redirectPath ? { path: redirectPath } : {}),
      analyticsIntent: funnelIntent,
    })
    await signIn()
  }

  useEffect(() => {
    if (currentUser) {
      redirectAfterLogin()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser])

  useEffect(() => {
    if (!router.isReady || currentUser) return

    getHostedAuthenticatedUser()
      .then(() => {
        redirectAfterLogin()
      })
      .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady, currentUser])

  const errorHandler =
    (id: string, alert: boolean = false) =>
    (message: string, testId: string) => {
      const payload = [...errors]
      const isCleared = !message || message === ''

      if (isCleared) return setErrors(payload.filter((f) => f.eleId !== testId))

      const isDuplicate = !!payload.find((p) => p.eleId === testId)
      const newError = {
        step: id ?? 'unknown',
        message,
        eleId: testId,
        alert,
      }

      if (!isDuplicate) setErrors([...payload, newError])
    }

  const handleSubmit = async (data: { [label: string]: string }) => {
    setLoading(true)
    setErrors([])
    try {
      const { email, firstName, lastName } = data

      if (!completedCaptcha) {
        setErrors((err) => [
          ...err,
          {
            step: 'captcha',
            message: 'Please complete the CAPTCHA to proceed.',
          },
        ])
        setLoading(false)
        return
      }

      const operationResult = await requestAccountCreation({
        variables: {
          firstName,
          lastName,
          email,
        },
      })

      if (
        operationResult?.errors ||
        operationResult?.data?.requestAccountCreation?.success === false
      ) {
        errorHandler('submit')(genericError, 'submit')
        setLoading(false)
        return
      }

      setFirstLoginFlag(true)
      setCompleted(true)
      setLoading(false)
    } catch (error: any) {
      console.log({ catch: error })
      errorHandler('submit')(genericError, 'submit')
      setLoading(false)
    }
  }

  if (completed) {
    return (
      <Box className="w-4/5 sm:w-1/2 mt-20">
        <Box className="flex flex-col space-y-2">
          <Box className="text-center text-2xl">
            <div className="flex justify-center mb-12">
              <Logo className="block w-12 mr-2" />
              <ForumLogoText fill={theme.textColour} className="h-6 w-24" />
            </div>
            <p>Please check your inbox</p>
          </Box>
        </Box>
        <Box className="flex justify-center text-center mx-auto">
          <Text>
            {`We've sent an email to the address you've provided. Click Set Password in that email to finish creating your account.`}
          </Text>
        </Box>
        <Box className="text-sm flex space-x-2 justify-center mt-8">
          <Link className="text-link hover:underline" href={loginHref}>
            Back to Log in
          </Link>
        </Box>
      </Box>
    )
  }

  return (
    <Box
      className={`flex flex-col w-full max-w-2xl items-center justify-start min-h-full pb-4`}
    >
      <Box className="w-4/5">
        <GoogleAds type="horizontal" show={false} />
      </Box>
      <Box className="mt-2 md:mt-4 w-full relative">
        <Box className="flex flex-col items-center space-y-2 mb-3 lg:mb-5 max-w-full">
          <Box className="text-center text-xl sm:text-2xl cursor-pointer relative">
            <Box
              className="relative mb-2 lg:mb-4 max-w-full flex items-center justify-center"
              onClick={() => (window.location.href = 'https://www.forumm.to')}
            >
              <ForumLogoText className="h-9 sm:h-12 lg:h-14" />
            </Box>
            <span className="font-extrabold max-w-full text-lg sm:text-2xl leading-tight">
              EVENTS FOR THE BOLD,
              <br /> THE WEIRD AND THE BRILLIANT.
            </span>
          </Box>
        </Box>
        <Box>
          <Form onSubmit={handleSubmit} error={errors?.[0]?.message}>
            <Box className="mt-8 mb-2">
              <Box className="mb-4 grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => beginHostedSignIn(signInWithGoogle)}
                  className="flex h-9 items-center justify-center gap-2 rounded border border-slate-400 bg-white px-3 text-xs font-medium text-slate-800 transition-colors hover:bg-slate-50"
                >
                  <FcGoogle className="h-4 w-4" />
                  <span>Google</span>
                </button>
                <button
                  type="button"
                  onClick={() => beginHostedSignIn(signInWithLinkedIn)}
                  className="flex h-9 items-center justify-center gap-2 rounded border border-slate-400 bg-white px-3 text-xs font-medium text-slate-800 transition-colors hover:bg-slate-50"
                >
                  <FaLinkedinIn className="h-4 w-4 text-[#0A66C2]" />
                  <span>LinkedIn</span>
                </button>
              </Box>
              <Box className="flex items-center gap-3 text-xs text-gray-500">
                <Box className="h-px flex-1 bg-gray-300" />
                <span>Quick sign up</span>
                <Box className="h-px flex-1 bg-gray-300" />
              </Box>
            </Box>
            <Box className="grid grid-cols-1 sm:grid-cols-2 gap-0 sm:gap-3 mb-0">
              <TextInput
                variation="v2"
                className="w-full mb-0"
                required
                validations={{
                  minLength: {
                    value: 2,
                    message: 'First Name must be at least 2 characters',
                  },
                  maxLength: {
                    value: 70,
                    message: 'First Name must be less than 70 characters',
                  },
                  pattern: {
                    value: /^[a-z ,.'-]+$/i,
                    message: 'Name must consist of letters only',
                  },
                }}
                label="First Name"
                placeholder="First Name"
              />

              <TextInput
                variation="v2"
                className="w-full mb-0"
                required
                validations={{
                  minLength: {
                    value: 2,
                    message: 'Last Name must be at least 2 characters',
                  },
                  maxLength: {
                    value: 70,
                    message: 'Last Name must be less than 70 characters',
                  },
                  pattern: {
                    value: /^[a-z ,.'-]+$/i,
                    message: 'Name must consist of letters only',
                  },
                }}
                label="Last Name"
                placeholder="Last Name"
              />
            </Box>

            <TextInput
              variation="v2"
              validations={{
                minLength: {
                  value: 7,
                  message: 'Email must be at least 7 characters',
                },
                maxLength: {
                  value: 70,
                  message: 'Email must be less than 70 characters',
                },
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Email must be valid',
                },
              }}
              required
              label="Email"
              type="email"
              placeholder="your@email.com"
            />

            <Box show={!!CAPTCHA_SITE_KEY} className="self-center mt-2">
              <Captcha
                className={'flex justify-center'}
                sitekey={CAPTCHA_SITE_KEY!}
                onChange={() => {
                  setCaptchaComplete(true)
                }}
                onError={() => {
                  setCaptchaComplete(false)
                }}
              />
              {errors.find((f) => f.step === 'captcha') ? (
                <Box
                  ignoreTheme
                  textColour="foregroundTextColour"
                  className="transition-all text-red-500 text-xs font-bold mt-1"
                >
                  {errors.find((f) => f.step === 'captcha')?.message}
                </Box>
              ) : (
                <></>
              )}
            </Box>
            <Button
              variation="v2"
              buttonType="submit"
              className="my-4 mt-5 w-full mb-0"
              title="Sign-up"
              type="primary"
              size="large"
              loading={loading}
              textColor="white"
            />
          </Form>
        </Box>

        <Box className="text-sm flex flex-wrap font-semibold gap-x-2 justify-center mt-2 text-center">
          <span className="">By signing up you agree to Forumm&apos;s</span>
          <Link
            className="text-[#ff00df] font-semibold hover:underline"
            href="/privacy"
          >
            Privacy Policy
          </Link>
        </Box>
        <Box className="text-sm flex flex-wrap font-semibold gap-x-2 justify-center mb-4 mt-2 text-center">
          <span className="">Already have an account?</span>
          <Link
            className="text-[#ff00df] font-semibold hover:underline"
            href={loginHref}
          >
            Sign-in Here
          </Link>
        </Box>
      </Box>
    </Box>
  )
}

export default SignUpForm
